import { useMemo } from 'react';
import { getVariant } from '../../../../../../utils/variant';
import useInput from '../../../../useInput';

/**
 * Custom hook that encapsulates all business logic for ProductQuantitySelectionContainer
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useProductQuantitySelection = ({
  maxCartQuantity,
  addToCartButtonVisible,
  errorAddingToCart,
  isAddToCartInProgress,
  isRemoveFromCartInProgress,
  handleRemoveFromCart,
  labels,
  handleAddToCart,
  isVendorLogin,
  sizes,
  selectedSize,
  setShowLoginForm,
  showLoginForm,
  setErrorAddingToCart,
  m2AttributeValue,
  layout,
  appCtx,
  product,
}) => {
  const quantityInput = useInput(maxCartQuantity);
  const { showQuantityError, inputVal, handleInputChange, setQuantityError, setInputVal } = quantityInput;

  const computedValues = useMemo(
    () => ({
      shouldShowCalculator: layout === 'hardline',

      // Determine if add to cart section should be rendered
      shouldShowAddToCart: Boolean(addToCartButtonVisible),

      // Determine if error warning should be shown
      shouldShowError: Boolean(errorAddingToCart),

      // Check if component is in loading state
      isLoading: isAddToCartInProgress || isRemoveFromCartInProgress,

      // Validate required props
      hasRequiredData: Boolean(maxCartQuantity && labels),

      // Generate CSS classes for container
      containerClasses: ['addToCart-container', 'fa--add-to-cart__desktop'].filter(Boolean).join(' '),
    }),
    [
      layout,
      addToCartButtonVisible,
      errorAddingToCart,
      isAddToCartInProgress,
      isRemoveFromCartInProgress,
      maxCartQuantity,
      labels,
    ]
  );

  const myListProps = useMemo(() => {
    if (!product || !product.id || !product.currentVariant || !product.variants) {
      return {
        product: {
          productId: '',
          variantId: '',
          offeringId: '',
          sellerId: '',
        },
        appCtx,
      };
    }

    const currentVariantData = getVariant(product.variants, product.currentVariant);
    const offerings = currentVariantData?.offerings || [];
    const firstOffering = offerings[0];

    // Try to get offeringId from multiple possible sources, fallback to variantId
    const offeringId =
      firstOffering?.offeringId ||
      firstOffering?.id ||
      currentVariantData?.offeringId ||
      currentVariantData?.id ||
      product.currentVariant; // Fallback to variantId

    return {
      product: {
        productId: product.id,
        variantId: product.currentVariant,
        offeringId,
        sellerId: firstOffering?.sellerId || '',
      },
      appCtx,
    };
  }, [product, appCtx]);

  // AddToCartButton props object - centralized prop management
  const addToCartButtonProps = useMemo(
    () => ({
      addToCartButtonVisible,
      isAddToCartInProgress,
      labels,
      maxCartQuantity,
      handleAddToCart,
      handleRemoveFromCart,
      isRemoveFromCartInProgress,
      isVendorLogin,
      sizes,
      selectedSize,
      setShowLoginForm,
      showLoginForm,
      setErrorAddingToCart,
      m2AttributeValue,
      showQuantityError,
      setQuantityError,
      inputVal,
      setInputVal,
      handleInputChange,
    }),
    [
      addToCartButtonVisible,
      isAddToCartInProgress,
      labels,
      maxCartQuantity,
      handleAddToCart,
      handleRemoveFromCart,
      isRemoveFromCartInProgress,
      isVendorLogin,
      sizes,
      selectedSize,
      setShowLoginForm,
      showLoginForm,
      setErrorAddingToCart,
      m2AttributeValue,
      showQuantityError,
      setQuantityError,
      inputVal,
      setInputVal,
      handleInputChange,
    ]
  );

  // GenericCalculatorContainer props
  const calculatorProps = useMemo(
    () => ({
      handleInputChange,
    }),
    [handleInputChange]
  );

  // Warning component props
  const warningProps = useMemo(
    () => ({
      label: errorAddingToCart,
      iconSize: 'copy10',
      labelSize: 'copy3',
      forPDP: true,
    }),
    [errorAddingToCart]
  );

  return {
    // Quantity input management
    quantityInput: {
      showQuantityError,
      inputVal,
      handleInputChange,
      setQuantityError,
      setInputVal,
    },

    // Computed values
    ...computedValues,

    // Component props objects
    addToCartButtonProps,
    calculatorProps,
    warningProps,
    myListProps,

    // Original props (for compatibility)
    originalProps: {
      maxCartQuantity,
      addToCartButtonVisible,
      errorAddingToCart,
      isAddToCartInProgress,
      isRemoveFromCartInProgress,
      handleRemoveFromCart,
      labels,
      handleAddToCart,
      isVendorLogin,
      sizes,
      selectedSize,
      setShowLoginForm,
      showLoginForm,
      setErrorAddingToCart,
      m2AttributeValue,
      layout,
    },
  };
};
